import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface VersionInfo {
  version: string;
  buildTime: number;
}

interface AppVersionTagProps {
  className?: string;
}

/**
 * Tag discret affichant la version déployée + le build time.
 * Triple-tap pour copier la chaîne complète dans le presse-papier (utile en support mobile).
 */
export function AppVersionTag({ className }: AppVersionTagProps) {
  const { t } = useTranslation('appui');
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: VersionInfo | null) => {
        if (!cancelled && data?.version) setInfo(data);
      })
      .catch(() => {
        /* silent — purement informatif */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;

  const formattedDate = (() => {
    try {
      const d = new Date(info.buildTime);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  })();

  const fullString = `v${info.version} · ${formattedDate}`;

  const handleTap = () => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 600);

    if (tapCountRef.current >= 3) {
      tapCountRef.current = 0;
      navigator.clipboard
        ?.writeText(fullString)
        .then(() => toast.success(t('appVersionTag.copied')))
        .catch(() => {
          /* ignore */
        });
    }
  };

  return (
    <button
      type="button"
      onClick={handleTap}
      className={cn(
        'select-text text-[10px] leading-none text-muted-foreground/60 hover:text-muted-foreground/90 transition-colors px-2 py-1 font-mono',
        className
      )}
      aria-label={`Application version ${fullString}`}
      title={t('appVersionTag.tripleClickToCopy')}
    >
      {fullString}
    </button>
  );
}
